import { createSignal } from "solid-js";

// LocaleService is an interface that defines the methods for managing
// locales in the application.
export interface LocaleService {
  getLocale: () => LocaleKey;
  setLocale: (locale: LocaleKey) => void;
}

// LocaleKey is every locale the apps can be switched to. It must stay in step
// with the translation catalogue: a locale a user can select and this type
// cannot hold is a locale that never reaches the services reading the wire.
export type LocaleKey =
  | "en"
  | "ko"
  | "de"
  | "pt"
  | "no"
  | "es"
  | "ru"
  | "zh"
  | "zh-CN"
  | "zh-TW"
  | "ms"
  | "fr"
  | "it"
  | "nl"
  | "he"
  | "ar"
  | "id";

type LocaleOptions = {
  [key in LocaleKey]: {
    name: string;
  };
};

// Names are written in the locale they name, which is what a language picker
// shows: somebody who cannot read the current interface language still
// recognises their own.
export const AvailableLocales: LocaleOptions = {
  en: { name: "English" },
  ko: { name: "한국어" },
  de: { name: "Deutsch" },
  pt: { name: "Português" },
  no: { name: "Norsk" },
  es: { name: "Español" },
  ru: { name: "Русский" },
  zh: { name: "中文" },
  "zh-CN": { name: "简体中文" },
  "zh-TW": { name: "繁體中文" },
  ms: { name: "Bahasa Melayu" },
  fr: { name: "Français" },
  it: { name: "Italiano" },
  nl: { name: "Nederlands" },
  he: { name: "עברית" },
  ar: { name: "العربية" },
  id: { name: "Bahasa Indonesia" },
};

const defaultLocale = "en" as LocaleKey;

// useLocaleService is a hook that provides the locale service which manages
// the state of the current locale and provides methods to get and set
// the locale. It may be used in conjunction with internationalization
// or localization libraries to provide a better user experience.
export const useLocaleService = () => {
  const [locale, setLocale] = createSignal(defaultLocale);

  return {
    getLocale: () => locale(),
    setLocale: (newLocale: LocaleKey) => setLocale(newLocale),
  };
};
